import { ChangeEvent, useEffect, useState } from 'react';

import { Button } from '@/components';
import { SelectInput } from '@/components/select-input/SelectInput';
import { TextInput } from '@/components/text-input';
import { useAppContext } from '@/context';
import { useUserUpdate } from '@/hooks';
import { useTranslation } from 'react-i18next';
import {
  getPycountryCompatibleCountries,
  getPycountryCompatibleCountryName,
  getCountryStateCityCompatibleCountryName
} from '@/utils/countryFilter';

import styles from '../Settings.module.css';

interface ShippingSectionProps {
  onUpdate: (user: Api.UserObject) => void;
  onFormChange: (data: null | Api.ShippingAddress) => void;
  user: Api.UserObject;
}

export function ShippingSection({ onUpdate, user, onFormChange }: ShippingSectionProps) {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [shippingAddress, setShippingAddress] = useState<Api.ShippingAddress>({
    name: '',
    co: '',
    state: '',
    address1: '',
    postal_code: '',
    door_code: '',
    city: '',
    country: ''
  });

  const { t } = useTranslation();
  const { setAlert } = useAppContext();

  const { mutate, isLoading } = useUserUpdate({
    shipping_address: shippingAddress,
    onSuccess: (res) => {
      if (res && (res as Api.UserObject).user_id) {
        onUpdate(res as Api.UserObject);
        onFormChange(null);
        setAlert('SAVED');
      }
    }
  });

  useEffect(() => {
    const pycountryCompatibleCountries = getPycountryCompatibleCountries();

    // Convert pycountry name back to country-state-city name for display
    const backendCountryName = user.influencer?.shipping_address.country;
    const displayCountryName = backendCountryName
      ? getCountryStateCityCompatibleCountryName(backendCountryName)
      : '';

    const country = pycountryCompatibleCountries.find((c) => c.name === displayCountryName);

    if (country) {
      setSelectedCountry(country.name);
    }

    setShippingAddress({
      name: user?.influencer?.shipping_address?.name || '',
      co: user?.influencer?.shipping_address?.co || '',
      state: user?.influencer?.shipping_address?.state || '',
      address1: user?.influencer?.shipping_address?.address1 || '',
      postal_code: user?.influencer?.shipping_address?.postal_code || '',
      door_code: user?.influencer?.shipping_address?.door_code || '',
      city: user?.influencer?.shipping_address?.city || '',
      country: country ? country.name : ''
    });
  }, []);

  function handleSelectChange(value: string) {
    setSelectedCountry(value);
    const pycountryCompatibleName = getPycountryCompatibleCountryName(value);
    setShippingAddress({
      ...shippingAddress,
      country: pycountryCompatibleName || ''
    });
    onFormChange({
      ...shippingAddress,
      country: pycountryCompatibleName || ''
    });
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    setShippingAddress((prevState: TypeAny) => ({
      ...prevState,
      [e.target.name]: e.target.value
    }));
    onFormChange({
      ...shippingAddress,
      [e.target.name]: e.target.value
    });
  }

  const { name, co, address1, door_code, city, postal_code, state } = shippingAddress;

  return (
    <div className={styles.section}>
      <div className={styles.sectionInfo}>
        <div className={styles.sectionInfoTitle}>{t('account.shippingSection.title')}</div>
        <div className={styles.sectionInfoDescription}>
          {t(
            user.is_agent
              ? 'account.shippingSection.descriptionAgent'
              : 'account.shippingSection.description'
          )}
        </div>
      </div>
      <div className={styles.sectionBody}>
        <div className={styles.sectionTable}>
          <div className={styles.row}>
            <div className={styles.grow}>
              <TextInput
                value={name}
                label={t('account.name')}
                name="name"
                required
                onChange={handleChange}
              />
              <TextInput value={co} label={t('account.co')} name="co" onChange={handleChange} />
              <TextInput
                value={address1}
                label={t('account.fullAddress')}
                name="address1"
                required
                onChange={handleChange}
              />
              <TextInput
                value={door_code}
                label={t('account.doorCode')}
                name="door_code"
                onChange={handleChange}
              />
              <TextInput
                value={city}
                label={t('account.city')}
                name="city"
                required
                onChange={handleChange}
              />
              <TextInput
                value={postal_code}
                label={t('account.postalCode')}
                name="postal_code"
                required
                onChange={handleChange}
              />
              <TextInput
                value={state}
                label={t('account.state')}
                name="state"
                onChange={handleChange}
              />
              <SelectInput
                value={selectedCountry}
                label={t('account.country')}
                options={getPycountryCompatibleCountries().map((country) => ({
                  value: country.name,
                  label: `${country.flag} ${country.name}`
                }))}
                required
                showSearch
                onChange={handleSelectChange}
              />
            </div>
            <div className={styles.fitContent} />
          </div>
        </div>
        <Button
          theme="cta"
          onClick={mutate}
          isLoading={isLoading}
          // disabled={!name || !address1 || !city || !postal_code || !selectedCountry}
          style={{ width: '140px' }}
        >
          {t('inviteView.addressModal.button')}
        </Button>
      </div>
    </div>
  );
}
